import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import LoadingSpinner from '../components/LoadingSpinner';
import { sendStatusNotification } from '../services/emailService';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending, approved, declined, all
  const [selectedApp, setSelectedApp] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const checkAdminAccess = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      navigate('/login');
      return;
    }

    // Check if user is admin
    const { data: adminData, error } = await supabase
      .from('admins')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .single();

    if (error || !adminData) {
      setError('Access denied. Admin privileges required.');
      setTimeout(() => navigate('/login'), 2000);
    }
  };

  const fetchApplications = async () => {
    setLoading(true);
    setError('');

    try {
      let query = supabase
        .from('application_forms')
        .select('*')
        .order('created_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query;

      if (error) throw error;

      setApplications(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAdminAccess();
    fetchApplications();
    
    // Subscribe to real-time updates
    const subscription = supabase
      .channel('application_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'application_forms' },
        () => fetchApplications()
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleStatusUpdate = async (applicationId, newStatus) => {
    setError('');
    setSuccess('');

    try {
      const { data: { user } } = await supabase.auth.getUser();

      // Get admin ID
      const { data: adminData } = await supabase
        .from('admins')
        .select('id')
        .eq('user_id', user.id)
        .single();

      // Update application status
      const { error: appError } = await supabase
        .from('application_forms')
        .update({
          status: newStatus,
          admin_notes: adminNotes,
          reviewed_by: adminData?.id,
          reviewed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', applicationId);

      if (appError) throw appError;

      // Update user status
      const application = applications.find(app => app.id === applicationId);
      const { error: userError } = await supabase
        .from('users')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', application.user_id);

      if (userError) throw userError;

      // Send email notification (you'll need to implement this with an email service)
      await sendEmailNotification(application, newStatus);

      setSuccess(`Application ${newStatus} successfully!`);
      setSelectedApp(null);
      setAdminNotes('');
      fetchApplications();

    } catch (err) {
      setError(err.message);
    }
  };

  const sendEmailNotification = async (application, status) => {
    try {
      // Send email using EmailJS service
      const result = await sendStatusNotification(application, status);
      
      if (!result.success) {
        console.error('Email notification failed:', result.error);
        // Don't throw error - continue with status update even if email fails
      }
    } catch (error) {
      console.error('Error sending email notification:', error);
      // Don't throw error - continue with status update even if email fails
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: '#CDB688', color: '#000' },
      approved: { bg: '#4CAF50', color: '#fff' },
      declined: { bg: '#f44336', color: '#fff' }
    };

    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: 'bold',
        background: styles[status]?.bg || '#999',
        color: styles[status]?.color || '#fff'
      }}>
        {status.toUpperCase()}
      </span>
    );
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="page-container">
      <div className="page-wrapper" style={{ maxWidth: '1200px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <h2 className="page-title" style={{ margin: 0 }}>Admin Dashboard</h2>
          <button 
            className="btn btn-secondary" 
            onClick={handleLogout}
            style={{ whiteSpace: 'nowrap' }}
          >
            Logout
          </button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert" style={{background: '#4CAF50', color: 'white'}}>{success}</div>}

        {/* Filter Tabs */}
        <div style={{ 
          display: 'flex', 
          gap: '0.5rem', 
          marginBottom: '1.5rem',
          flexWrap: 'wrap'
        }}>
          {['pending', 'approved', 'declined', 'all'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                border: 'none',
                background: filter === status ? '#D4AF37' : '#333',
                color: 'white',
                fontWeight: filter === status ? 'bold' : 'normal',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                fontSize: '0.85rem',
                whiteSpace: 'nowrap',
                flex: '0 1 auto'
              }}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)} 
              <span style={{ 
                display: 'inline-block',
                marginLeft: '0.25rem'
              }}>
                ({applications.filter(app => status === 'all' || app.status === status).length})
              </span>
            </button>
          ))}
        </div>

        {/* Applications List */}
        <div style={{ display: 'grid', gap: '1rem' }}>
          {applications.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
              <p>No applications found for this filter.</p>
            </div>
          ) : (
            applications.map(app => (
              <div key={app.id} className="card" style={{ padding: '1rem' }}>
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  <div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.75rem', 
                      marginBottom: '0.75rem',
                      flexWrap: 'wrap'
                    }}>
                      <h3 style={{ 
                        margin: 0, 
                        color: '#D4AF37',
                        fontSize: '1.1rem',
                        wordBreak: 'break-word'
                      }}>
                        {app.first_name} {app.last_name}
                      </h3>
                      {getStatusBadge(app.status)}
                    </div>
                    
                    <div style={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                      marginTop: '1rem',
                      fontSize: '0.85rem'
                    }}>
                      <div style={{ wordBreak: 'break-word' }}>
                        <strong>Email:</strong> {app.email}
                      </div>
                      <div>
                        <strong>Phone:</strong> {app.phone || 'N/A'}
                      </div>
                      <div>
                        <strong>Tier:</strong> {app.membership_tier}
                      </div>
                      <div>
                        <strong>Applied:</strong> {new Date(app.created_at).toLocaleDateString()}
                      </div>
                      {app.date_of_birth && (
                        <div>
                          <strong>DOB:</strong> {new Date(app.date_of_birth).toLocaleDateString()}
                        </div>
                      )}
                      {app.occupation && (
                        <div style={{ wordBreak: 'break-word' }}>
                          <strong>Occupation:</strong> {app.occupation}
                        </div>
                      )}
                    </div>

                    {app.areas_of_interest && app.areas_of_interest.length > 0 && (
                      <div style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>
                        <strong>Additional Info:</strong> {app.areas_of_interest.join(', ')}
                      </div>
                    )}

                    {app.admin_notes && (
                      <div style={{ 
                        marginTop: '0.75rem',
                        padding: '0.5rem',
                        background: '#f5f5f5',
                        borderRadius: '4px',
                        fontSize: '0.85rem'
                      }}>
                        <strong>Admin Notes:</strong> {app.admin_notes}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  {app.status === 'pending' && (
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <button
                        onClick={() => setSelectedApp(app)}
                        className="btn btn-primary"
                        style={{ 
                          fontSize: '0.85rem', 
                          padding: '0.6rem 1.2rem',
                          flex: '1 1 auto'
                        }}
                      >
                        Review
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Review Modal */}
        {selectedApp && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <div className="card" style={{ 
              maxWidth: '500px', 
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: '1rem'
            }}>
              <h3 style={{ 
                color: '#D4AF37', 
                marginBottom: '1rem',
                fontSize: '1.2rem'
              }}>
                Review Application
              </h3>
              
              <div style={{ marginBottom: '1rem', fontSize: '0.9rem' }}>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Name:</strong> {selectedApp.first_name} {selectedApp.last_name}
                </p>
                <p style={{ marginBottom: '0.5rem', wordBreak: 'break-word' }}>
                  <strong>Email:</strong> {selectedApp.email}
                </p>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Phone:</strong> {selectedApp.phone}
                </p>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Membership:</strong> {selectedApp.membership_tier}
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Admin Notes (Optional)</label>
                <textarea
                  className="form-input"
                  rows="4"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add any notes about this application..."
                  style={{ fontSize: '0.9rem' }}
                />
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '0.5rem',
                marginTop: '1rem',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => handleStatusUpdate(selectedApp.id, 'approved')}
                  className="btn btn-primary"
                  style={{ 
                    flex: '1 1 120px', 
                    background: '#4CAF50',
                    fontSize: '0.9rem',
                    padding: '0.6rem 1rem'
                  }}
                >
                  Approve
                </button>
                <button
                  onClick={() => handleStatusUpdate(selectedApp.id, 'declined')}
                  className="btn btn-secondary"
                  style={{ 
                    flex: '1 1 120px', 
                    background: '#f44336', 
                    color: 'white',
                    fontSize: '0.9rem',
                    padding: '0.6rem 1rem'
                  }}
                >
                  Decline
                </button>
                <button
                  onClick={() => {
                    setSelectedApp(null);
                    setAdminNotes('');
                  }}
                  className="btn btn-secondary"
                  style={{ 
                    flex: '1 1 120px',
                    fontSize: '0.9rem',
                    padding: '0.6rem 1rem'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
